import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useDarkMode } from '../../../contexts/DarkModeContext';
import useAuthStore from '../../../store/authStore';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { db } from '../../../firebase';
import ChatAppPopup from './ChatAppPopup';

const FloatingChatIcon = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [justIncreased, setJustIncreased] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const { isDarkMode } = useDarkMode();
  const location = useLocation();
  const prevUnreadRef = useRef(0);
  const { user } = useAuthStore();
  const unreadListenersRef = useRef({});

  // ⚡️ GLOBAL FIRESTORE LISTENER: Real-time unread count updates even when popup is closed
  useEffect(() => {
    if (!user || !user.email) return;

    const isAdmin = user.role === 'admin' || user.isAdmin;
    const conversationUnreadCounts = {};

    // Function to update total unread count
    const updateTotalUnread = () => {
      const totalUnread = Object.values(conversationUnreadCounts).reduce((sum, count) => sum + count, 0);
      const prevCount = prevUnreadRef.current;
      
      if (totalUnread > prevCount) {
        setJustIncreased(true);
        setTimeout(() => setJustIncreased(false), 700);
      }
      
      prevUnreadRef.current = totalUnread;
      setUnreadCount(totalUnread);
      window.__unreadTotal = totalUnread;
      window.dispatchEvent(new CustomEvent('unreadTotalChanged', { detail: totalUnread }));
    };

    // Listen to all conversations for the current user
    const conversationsQuery = query(
      collection(db, 'sellerbuyerconversations'),
      where(isAdmin ? 'adminEmail' : 'members', 'array-contains', user.email)
    );

    const conversationsUnsubscribe = onSnapshot(conversationsQuery, (conversationsSnapshot) => {
      // Clean up previous message listeners
      Object.values(unreadListenersRef.current).forEach(unsubscribe => unsubscribe());
      unreadListenersRef.current = {};

      // Reset conversation unread counts
      Object.keys(conversationUnreadCounts).forEach(key => delete conversationUnreadCounts[key]);

      if (conversationsSnapshot.docs.length === 0) {
        // No conversations, set unread to 0
        setUnreadCount(0);
        window.__unreadTotal = 0;
        window.dispatchEvent(new CustomEvent('unreadTotalChanged', { detail: 0 }));
        return;
      }

      conversationsSnapshot.docs.forEach((convDoc) => {
        const convId = convDoc.id;
        const messagesQuery = query(
          collection(db, 'sellerbuyerconversations', convId, 'messages'),
          orderBy('timestamp', 'desc')
        );

        unreadListenersRef.current[convId] = onSnapshot(messagesQuery, (messagesSnapshot) => {
          let convUnreadCount = 0;
          
          messagesSnapshot.docs.forEach((msgDoc) => {
            const msgData = msgDoc.data();
            
            if (isAdmin) {
              if (
                msgData.from !== user.email &&
                msgData.from !== 'ytproudltd@gmail.com' &&
                msgData.readByAdmin !== true
              ) {
                convUnreadCount += 1;
              }
            } else {
              if (msgData.from !== user.email && (!msgData.readBy || !msgData.readBy.includes(user.email))) {
                convUnreadCount += 1;
              }
            }
          });

          // Update this conversation's unread count
          conversationUnreadCounts[convId] = convUnreadCount;
          
          // Update total unread count immediately
          updateTotalUnread();
        });
      });
    });

    return () => {
      conversationsUnsubscribe();
      Object.values(unreadListenersRef.current).forEach(unsubscribe => unsubscribe());
    };
  }, [user]);

  // Listen for unread count changes from Sidebar (backup)
  useEffect(() => {
    const handler = (event) => {
      const next = event.detail;
      if (next > prevUnreadRef.current) {
        setJustIncreased(true);
        setTimeout(() => setJustIncreased(false), 700);
      }
      prevUnreadRef.current = next;
      setUnreadCount(next);
    };
    
    window.addEventListener('unreadTotalChanged', handler);
    
    // Initialize on mount
    if (typeof window.__unreadTotal === 'number') {
      handler({ detail: window.__unreadTotal });
    }
    
    return () => window.removeEventListener('unreadTotalChanged', handler);
  }, []);

  // Always show the chat icon
  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Listen for openChatPopup event from MobileFooterBar
  useEffect(() => {
    const handleOpenChatPopup = () => {
      setIsChatOpen(true);
    };
    
    window.addEventListener('openChatPopup', handleOpenChatPopup);
    
    return () => window.removeEventListener('openChatPopup', handleOpenChatPopup);
  }, []);

  // Dispatch chat state events for MobileFooterBar
  useEffect(() => {
    if (isChatOpen) {
      window.dispatchEvent(new CustomEvent('chatOpened'));
    } else {
      window.dispatchEvent(new CustomEvent('chatClosed'));
    }
  }, [isChatOpen]);

  const handleClick = () => {
    setIsChatOpen(true);
  };

  // Hide on messages page
  if (location.pathname === '/messages' || location.pathname.startsWith('/messages')) {
    return null;
  }

  return (
    <>
      <div
        className="fixed right-6 bottom-6 z-50 transition-all duration-300 ease-out hidden md:block"
      >
        <button
          onClick={handleClick}
          className={`group relative flex items-center justify-center w-14 h-14 rounded-full shadow-2xl transition-all duration-300 hover:scale-110 hover:shadow-3xl ${
            isDarkMode
              ? 'bg-gradient-to-br from-blue-600 via-blue-500 to-blue-400 hover:from-blue-500 hover:via-blue-400 hover:to-blue-300 shadow-blue-500/25'
              : 'bg-gradient-to-br from-blue-500 via-blue-400 to-blue-300 hover:from-blue-400 hover:via-blue-300 hover:to-blue-200 shadow-blue-400/30'
          } ${justIncreased ? 'chat-icon-pulse' : (unreadCount > 0 ? 'chat-icon-attention' : '')}`}
          title="Chat with us"
          aria-label="Open chat"
        >
          {/* Modern chat icon */}
          <svg
            className="w-6 h-6 text-white transition-transform duration-300 group-hover:scale-110"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>

          {/* Unread badge */}
          {unreadCount > 0 && (
            <span
              className={`absolute -top-2 -right-2 min-w-[22px] h-[22px] px-1.5 inline-flex items-center justify-center rounded-full bg-red-500 text-white text-xs font-bold shadow-lg ring-2 ring-white transition-all duration-300 ${
                justIncreased ? 'badge-pop' : 'badge-attention'
              }`}
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}

          {/* Ripple effect on hover */}
          <div className="absolute inset-0 rounded-full bg-white/20 scale-0 group-hover:scale-100 transition-transform duration-300" />
          
          {/* Glow effect */}
          <div
            className={`absolute inset-0 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${
              isDarkMode ? 'bg-blue-400/30' : 'bg-blue-300/40'
            }`}
          />
        </button>

        {/* Tooltip */}
        <div className="absolute right-16 top-1/2 -translate-y-1/2 bg-gray-900 text-white text-sm px-3 py-2 rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none whitespace-nowrap">
          Chat with us
          <div className="absolute left-full top-1/2 -translate-y-1/2 w-0 h-0 border-l-4 border-l-gray-900 border-t-4 border-t-transparent border-b-4 border-b-transparent" />
        </div>
      </div>

      {/* Chat App Popup */}
      <ChatAppPopup isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} />
    </>
  );
};

export default FloatingChatIcon;
